import React, { useEffect, useState } from "react";
import { apiRequest } from "../../../../service/apiRequest";
import {
  Table,
  Button,
  Flex,
  Breadcrumb,
  Form,
  message,
  Popconfirm,
  Space,
  Pagination,
  Switch,
  TableProps,
  Select,
  Input,
} from "antd";
import type { PopconfirmProps, TableColumnsType } from "antd";
import { createStyles } from "antd-style";
import { useSearchParams, useNavigate } from "react-router-dom";
import { HomeOutlined, DeleteOutlined, EditOutlined, SearchOutlined } from "@ant-design/icons";
import { theme } from "antd";
import ProductModal from "./ProductModal";
import { showSuccess, showError } from "../../../../components/errorHandler";
import { useCurrentModule } from "../../../../hooks/useCurrentModule";

import CopyButton from "../../../../components/copyButton";
import Barcode from "../../../../components/Barcode";
import ImportProductModal from "./ImportProductModal";

const { Option } = Select;
interface BarcodeProps {
  value: string; // the product_code to encode
}
const useStyle = createStyles(({ css }) => ({
  customTable: css`
    .ant-table {
      &-container {
        .ant-table-body,
        .ant-table-content {
          scrollbar-width: thin;
          scrollbar-color: #eaeaea transparent;
        }
      }
    }
  `,
}));

interface Products {
  product_id: number;
  product_name: string;
  product_code: string;
  ut_name: string;
  product_qty: string;
  category_name: string;
  category_id: number;
  product_unit: number;
  brand_id: number;
  brand_name: string;
  product_price: number;
  product_status: number;
}
const Products: React.FC = () => {
  const navigate = useNavigate();
  const [categories, setCategories] = useState<
    { category_id: number; category_name: string }[]
  >([]);
  const [products, setProducts] = useState<Products[]>([]);
  const [form] = Form.useForm();
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);
  const [initialValues, setInitialValues] = useState<Products | null>(null);
  const { styles } = useStyle();
  const [isImportModalVisible, setIsImportModalVisible] = useState(false);
  const [pagination, setPagination] = useState<{
    current: number;
    pageSize: number;
    total: number;
  }>({
    current: 1,
    pageSize: 10,
    total: 0,
  });
  const currentModule = useCurrentModule();

  const [filteredInfo, setFilteredInfo] = useState<Record<string, any>>({});
  const [sortedInfo, setSortedInfo] = useState<any>({});

  const [searchParams, setSearchParams] = useSearchParams();

  // 2. Read the current filters from URL
  const selectedCategoryId = searchParams.get("category") || undefined;
  const selectedKeyword = searchParams.get("keyword") || "";

  // 3. Local state for controlled inputs
  const [categoryFilter, setCategoryFilter] = useState<string | undefined>(
    selectedCategoryId,
  );
  const [keywordFilter, setKeywordFilter] = useState<string>(selectedKeyword);

  const cancel: PopconfirmProps["onCancel"] = (e) => {
    console.log(e);
    showError("Click on No");
  };

  const {
    token: { colorBgContainer, borderRadiusLG },
  } = theme.useToken();

  const fetchProduct = async (
    page = 1,
    limit = 10,
    category_id?: string,
    category_keyword?: string,
  ) => {
    let url = `get-handicraft-products?page=${page}&limit=${limit}`;
    if (category_id) url += `&category_id=${category_id}`;
    if (category_keyword) url += `&keyword=${category_keyword}`;

    const response = await apiRequest({
      method: "get",
      url,
    });
    setProducts(response.data);
    setPagination({
      current: response.page,
      pageSize: response.limit,
      total: response.total,
    });
  };

  const fetchCategories = async () => {
    try {
      const response = await apiRequest({
        method: "get",
        url: "category?status=1",
      });
      setCategories(response || []);
    } catch (error) {
      showError("Failed to fetch categories");
    }
  };

  useEffect(() => {
    fetchCategories();
  }, []);

  useEffect(() => {
    const cat = searchParams.get("category") || undefined;
    const kw = searchParams.get("keyword") || "";
    setCategoryFilter(cat);
    setKeywordFilter(kw);
    fetchProduct(
      pagination.current,
      pagination.pageSize,
      cat,
      kw,
    );
  }, [searchParams]);

  const handleSearch = (kwToSearch?: string) => {
    const kw = kwToSearch !== undefined ? kwToSearch : keywordFilter;
    const trimmed = (kw || "").trim();
    const params = new URLSearchParams(searchParams.toString());
    const prevKw = searchParams.get("keyword") || "";
    if (trimmed) {
      params.set("keyword", trimmed);
    } else {
      params.delete("keyword");
    }
    if (prevKw === trimmed) {
      fetchProduct(
        1,
        pagination.pageSize,
        categoryFilter,
        trimmed,
      );
    } else {
      setSearchParams(params);
    }
  };

  const handleEditClick = (record: Products) => {
    setIsEditMode(true);
    setInitialValues({
      product_id: record.product_id,
      product_name: record.product_name,
      product_status: record.product_status,
      product_code: record.product_code,
      product_unit: record.product_unit,
      product_price: record.product_price,
      ut_name: record.ut_name,
      product_qty: record.product_qty,
      category_name: record.category_name,
      category_id: record.category_id,
      brand_name: record.brand_name,
      brand_id: record.brand_id,
    });
    setIsModalVisible(true);
  };

  const handleCreate = (data: any) => {
    fetchProduct(
      pagination.current,
      pagination.pageSize,
      selectedCategoryId,
      selectedKeyword,
    );
    if (data && data.product_id && data.category_name) {
      if (data.category_name.toLowerCase().trim() === "packed item") {
        navigate(`/bom?pid=${data.product_id}`);
      }
    }
  };

  const showCreateModal = () => {
    setIsEditMode(false);
    setInitialValues(null);
    setIsModalVisible(true);
  };

  const hideCreateModal = () => {
    setIsModalVisible(false);
    setIsEditMode(false);
    setInitialValues(null);
  };

  const handleDelete = async (product_id: number) => {
    try {
      await apiRequest({
        method: "delete",
        url: `/delete-handicraft-product/${product_id}`,
      });
      showSuccess("Product deleted");
      fetchProduct(
        pagination.current,
        pagination.pageSize,
        selectedCategoryId,
        selectedKeyword,
      );
    } catch {
      showError("Failed to delete Product");
    }
  };

  const handleStatusToggle = async (productId: number) => {
    setProducts((prev) =>
      prev.map((item) =>
        item.product_id === productId
          ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
          : item,
      ),
    );

    try {
      await apiRequest({
        method: "put",
        url: `/update-status-handicraft-product/${productId}`,
      });
      showSuccess("Product status updated");
    } catch (error) {
      showError("Failed to update status");
      setProducts((prev) =>
        prev.map((item) =>
          item.product_id === productId
            ? { ...item, product_status: item.product_status === 1 ? 0 : 1 }
            : item,
        ),
      );
    }
  };

  const handleTableChange: TableProps<Products>["onChange"] = (
    pagination,
    filters,
    sorter,
  ) => {
    setFilteredInfo(filters);
    setSortedInfo(sorter);
  };

  const handleImportSuccess = () => {
    fetchProduct(
      pagination.current,
      pagination.pageSize,
      selectedCategoryId,
      selectedKeyword,
    );
  };

  // पुराने दोनों useEffect हटाएं और यह लगाएं
  useEffect(() => {
    const keyword = searchParams.get("keyword") || "";
    const category = searchParams.get("category") || undefined;

    setKeywordFilter(keyword);
    setCategoryFilter(category);

    fetchProduct(1, pagination.pageSize, category, keyword);
  }, []); // ← सिर्फ mount पर, searchParams dependency नहीं

  const columns: TableColumnsType<Products> = [
    {
      title: "Product Name",
      dataIndex: "product_name",
      key: "product_name",
      filters: Array.from(new Set(products.map((p) => p.product_name))).map(
        (name) => ({
          text: name,
          value: name,
        }),
      ),
      filterSearch: true,
      filteredValue: filteredInfo.product_name || null,
      onFilter: (value, record) =>
        record.product_name.includes(value as string),
      sorter: (a, b) => a.product_name.localeCompare(b.product_name),
      sortOrder:
        sortedInfo.columnKey === "product_name" ? sortedInfo.order : null,
      sortDirections: ["ascend", "descend"],
      ellipsis: true,
    },
    {
      title: "Category ",
      dataIndex: "category_name",
      key: "category_name",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>{record.category_name || "N/A"}</span>
          {record.category_name && (
            <CopyButton textToCopy={record.category_name} />
          )}
        </div>
      ),
    },
    {
      title: "Brand Name ",
      dataIndex: "brand_name",
      key: "brand_name",
      render: (_, record) => (
        <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
          <span>{record.brand_name || "N/A"}</span>
          {record.brand_name && <CopyButton textToCopy={record.brand_name} />}
        </div>
      ),
    },
    // {
    //   title: 'Product Code',
    //   dataIndex: 'product_code',
    //   key: 'product_code',
    //   render: (_, record) => (
    //     <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    //       <span>{record.product_code || 'N/A'}</span>
    //       {record.product_code && <CopyButton textToCopy={record.product_code} />}
    //     </div>
    //   ),
    // },
    // {
    //   title: 'Bar Code',
    //   dataIndex: 'product_code',
    //   key: 'product_code',
    //   render: (_, record) => (
    //     <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
    //       {record.product_code && <Barcode value={record.product_code} />}
    //     </div>
    //   ),
    // },
    {
      title: "Price",
      dataIndex: "product_price",
      key: "product_price",
      sorter: (a, b) => a.product_price - b.product_price,
      sortOrder:
        sortedInfo.columnKey === "product_price" ? sortedInfo.order : null,
      render: (_, record) => <span>₹{record.product_price.toFixed(4)}</span>,
    },
    {
      title: "Qty",
      dataIndex: "product_qty",
      key: "product_qty",
      sortOrder:
        sortedInfo.columnKey === "product_qty" ? sortedInfo.order : null,
      render: (_, record) => (
        <span>
          {record.product_qty} {record.ut_name}
        </span>
      ),
    },
  ];

  // Conditionally add action column
  if (
    currentModule?.right_edit === "Y" ||
    currentModule?.right_delete === "Y"
  ) {
    columns.push({
      title: "Action",
      width: 200,
      fixed: "right",
      render: (_: any, record: Products) => (
        <Space size="middle">
          {currentModule?.right_edit === "Y" && (
            <>
              <Switch
                checkedChildren="Active"
                unCheckedChildren="Inactive"
                checked={!!record.product_status}
                onChange={() => handleStatusToggle(record.product_id)}
              />
              <Button onClick={() => handleEditClick(record)} className="px-8">
                <EditOutlined />
              </Button>
            </>
          )}
          {currentModule?.right_delete === "Y" && (
            <Popconfirm
              title="Delete this product?"
              onConfirm={() => handleDelete(record.product_id)}
              onCancel={cancel}
              okText="Yes"
              cancelText="No"
            >
              <Button danger className="px-8">
                <DeleteOutlined />
              </Button>
            </Popconfirm>
          )}
        </Space>
      ),
    });
  }

  return (
    <>
      <Space
        style={{
          display: "flex",
          marginBottom: 16,
          alignItems: "center",
          width: "100%",
          justifyContent: "space-between",
        }}
      >
        <Breadcrumb style={{ display: "inline-block", margin: 0 }}>
          <Breadcrumb.Item href="/">
            <HomeOutlined />
          </Breadcrumb.Item>
          <Breadcrumb.Item>View Products</Breadcrumb.Item>
        </Breadcrumb>
        <Flex gap="small" style={{ marginBottom: 16 }}>
          <Select
            placeholder="Filter by Category"
            allowClear
            style={{ minWidth: 200, marginTop: "8px" }}
            onChange={(value) => {
              setCategoryFilter(value);

              // Update URL
              const params = new URLSearchParams(searchParams.toString());
              if (value) params.set("category", value.toString());
              else params.delete("category");
              setSearchParams(params);

              // Fetch products
              fetchProduct(
                1,
                pagination.pageSize,
                value,
                keywordFilter,
              );
            }}
            value={categoryFilter || undefined}
          >
            <Option value="">All</Option>
            {categories.map((cat) => (
              <Option key={cat.category_id} value={cat.category_id}>
                {cat.category_name}
              </Option>
            ))}
          </Select>

          <Space.Compact style={{ marginTop: "8px" }}>
            <Input
              placeholder="Search Product Name"
              style={{ minWidth: 260 }}
              value={keywordFilter}
              onChange={(e) => {
                const val = e.target.value;
                setKeywordFilter(val);
                if (!val && searchParams.has("keyword")) {
                  const params = new URLSearchParams(searchParams.toString());
                  params.delete("keyword");
                  setSearchParams(params);
                  fetchProduct(1, pagination.pageSize, categoryFilter, "");
                }
              }}
              onPressEnter={() => handleSearch()}
              allowClear
            />
            <Button
              type="primary"
              icon={<SearchOutlined />}
              onClick={() => handleSearch()}
            >
              Search
            </Button>
          </Space.Compact>
        </Flex>

        <Flex gap="small">
          {currentModule?.right_add === "Y" && (
            <Flex gap="small">
              <Button type="primary" onClick={showCreateModal}>
                Add Product
              </Button>
            </Flex>
          )}
          {currentModule?.right_import === "Y" && (
            <Flex gap="small">
              <Button
                type="primary"
                onClick={() => setIsImportModalVisible(true)}
              >
                Import Product
              </Button>
            </Flex>
          )}
        </Flex>
      </Space>

      <div
        style={{
          minHeight: 360,
          background: colorBgContainer,
          borderRadius: borderRadiusLG,
        }}
      >
        <Table<Products>
          tableLayout="auto"
          className={`${styles.customTable} scrollable-table`}
          columns={columns}
          onChange={handleTableChange}
          rowKey="product_id"
          dataSource={products}
          bordered
          pagination={false}
          scroll={{ y: 37 * 15 }}
          locale={{ emptyText: "No product found" }}
          footer={() => (
            <Flex justify="space-between">
              <span>
                Showing {(pagination.current - 1) * pagination.pageSize + 1}–
                {Math.min(
                  pagination.current * pagination.pageSize,
                  products.length,
                )}{" "}
                of {products.length}
              </span>
              <Pagination
                current={pagination.current}
                pageSize={pagination.pageSize}
                total={pagination.total}
                showSizeChanger
                pageSizeOptions={["10", "20", "50", "100"]}
                onChange={(page, pageSize) => {
                  setPagination((prev) => ({
                    ...prev,
                    current: page,
                    pageSize,
                  }));
                  fetchProduct(page, pageSize, categoryFilter, keywordFilter);
                }}
              />
            </Flex>
          )}
        />
      </div>

      <ProductModal
        visible={isModalVisible}
        onCancel={hideCreateModal}
        onCreate={handleCreate}
        isEditMode={isEditMode}
        initialValues={initialValues}
      />
      <ImportProductModal
        visible={isImportModalVisible}
        onClose={() => setIsImportModalVisible(false)}
        onSuccess={handleImportSuccess}
      />
    </>
  );
};

export default Products;
